// src/backend/services/feed/FeedService.ts
import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter, 
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove,
  getDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../../config/firebase-config";
import { Post, Media, LinkPreview } from "../../../models/Post";

const POSTS_COLLECTION = "posts";

export const FeedService = {
  createPost: async (
    userId: string, 
    user: string, 
    username: string, 
    userProfilePicture: string, 
    content: string, 
    tag: string,
    title?: string,
    mediaFiles?: Array<{file: File, type: 'image'|'video'|'pdf'}>,
    linkPreview?: LinkPreview,
    userRole?: 'Business Owner' | 'Employee' | 'Student',
    affiliation?: string,
    isSubscribed: boolean = false
  ) => {
    try {
      const mediaData: Media[] = [];

      // Upload media if present
      if (mediaFiles && mediaFiles.length > 0) {
        for (const fileObj of mediaFiles) {
          if (!fileObj.file) continue;
          
          const fileExtension = fileObj.file.name.split('.').pop();
          const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExtension}`;
          const storagePath = `posts/${userId}/${fileName}`;
          const storageRef = ref(storage, storagePath);
          
          await uploadBytes(storageRef, fileObj.file);
          const downloadUrl = await getDownloadURL(storageRef);
          
          mediaData.push({
            type: fileObj.type,
            preview: downloadUrl, // Store preview/thumbnail logic if needed, simplify to URL for now
            url: downloadUrl,
            path: storagePath
          });
        }
      }

      const postData = {
        userId,
        user,
        username,
        profilePicture: userProfilePicture,
        title: title || null,
        content,
        tag,
        timestamp: serverTimestamp(),
        media: mediaData,
        linkPreview: linkPreview || null,
        likes: 0,
        comments: 0,
        reposts: 0,
        likedBy: [],
        userRole: userRole || 'Business Owner',
        affiliation: affiliation || '',
        isSubscribed: isSubscribed
      };

      const docRef = await addDoc(collection(db, POSTS_COLLECTION), postData);
      return { id: docRef.id, ...postData };
    } catch (error) {
      console.error("Error creating post:", error);
      throw error;
    }
  },

  getPosts: async (lastVisible: any = null, category: string = 'all', paginationLimit: number = 10, userIdsFilter?: string[]) => {
    try {
      let q = query(
        collection(db, POSTS_COLLECTION),
        orderBy("timestamp", "desc"),
        limit(paginationLimit)
      );

      // If userIdsFilter is provided (for Following tab), use it
      if (userIdsFilter && userIdsFilter.length > 0) {
        // Firestore 'in' query supports up to 10 values. For MVP we slice.
        // For production we'd need multiple queries or a different data model.
        const safeUserIds = userIdsFilter.slice(0, 10); 
        q = query(
          collection(db, POSTS_COLLECTION),
          where("userId", "in", safeUserIds),
          orderBy("timestamp", "desc"),
          limit(paginationLimit)
        );
      }
      else if (category !== 'all') {
         q = query(
          collection(db, POSTS_COLLECTION),
          where("tag", "==", category),
          orderBy("timestamp", "desc"),
          limit(paginationLimit)
        );
      }

      if (lastVisible) {
        q = query(q, startAfter(lastVisible));
      }

      const querySnapshot = await getDocs(q);
      const posts: Post[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        posts.push({
          id: doc.id,
          ...data
        } as Post);
      });
      
      return {
        posts,
        lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1]
      };
    } catch (error) {
      console.error("Error fetching posts:", error);
      throw error;
    }
  },

  toggleLike: async (postId: string, userId: string, isLiked: boolean) => {
    try {
      const postRef = doc(db, POSTS_COLLECTION, postId);
      
      if (isLiked) {
        // Unlike
        await updateDoc(postRef, {
          likes: increment(-1),
          likedBy: arrayRemove(userId)
        });
      } else {
        // Like
        await updateDoc(postRef, {
          likes: increment(1),
          likedBy: arrayUnion(userId)
        });
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      throw error;
    }
  },

  deletePost: async (postId: string) => {
    try {
        await deleteDoc(doc(db, POSTS_COLLECTION, postId));
    } catch (error) {
        console.error("Error deleting post:", error);
        throw error;
    }
  }
};
